import type { Promocion } from '@/lib/precios'
import type { PromocionCruda } from './tipos'

// Traduce las promociones que sirve el host al modelo que come el motor.
//
// El host las manda con los arreglos todavía serializados (`productoIdsJson`,
// `diasSemanaJson`) porque en su base son columnas de texto: una lista corta
// de ids no justifica una tabla puente que además agregaría un join a la
// consulta más caliente del POS.
//
// Un JSON corrupto en una promoción no puede romper la pantalla de venta: se
// ignora esa lista y la promoción no alcanza a nadie. Es exactamente el mismo
// criterio que en el traductor de C# (Caja/Ventas/PromocionesLocales.cs).
export function traducirPromociones(crudas: PromocionCruda[]): Promocion[] {
  return crudas.map((p) => ({
    id: p.id,
    version: p.version,
    nombre: p.nombre,

    // Los tipos vienen como texto del servidor. Si esta versión del bundle no
    // conoce el valor, la promoción no aplica: el motor la ignora. Es lo
    // correcto —aplicar «algo parecido» a lo que la nube quiso decir es peor
    // que no aplicar nada— y el caso real es un bundle viejo con una nube
    // nueva.
    tipo: p.tipo as Promocion['tipo'],
    modo: p.modo as Promocion['modo'],
    alcance: p.alcance as Promocion['alcance'],

    productoId: p.productoId ?? null,
    categoriaId: p.categoriaId ?? null,
    productoIds: enteros(p.productoIdsJson),

    cantidadRequeridaMilesimas: p.cantidadRequeridaMilesimas,
    cantidadPagaMilesimas: p.cantidadPagaMilesimas,
    precioGrupoCentavos: p.precioGrupoCentavos,
    porcentajeBps: p.porcentajeBps,
    importeCentavos: p.importeCentavos,

    // Las fechas llegan como ISO corto o como fecha completa según el host;
    // se recorta a los diez primeros caracteres, que es lo que el motor
    // compara.
    desdeFecha: p.desdeFecha ? p.desdeFecha.slice(0, 10) : null,
    hastaFecha: p.hastaFecha ? p.hastaFecha.slice(0, 10) : null,

    diasSemana: enteros(p.diasSemanaJson),
    desdeMinuto: p.desdeMinuto ?? null,
    hastaMinuto: p.hastaMinuto ?? null,

    prioridad: p.prioridad,
    combinable: p.combinable,
    activa: p.activa,
  }))
}

function enteros(json: string | null | undefined): number[] {
  if (!json) return []

  try {
    // Se tipa como unknown y no se deja el `any` que devuelve JSON.parse: con
    // any, el filter de abajo no chequea nada y un JSON con strings pasaría
    // como si fueran números.
    const valor: unknown = JSON.parse(json)
    return Array.isArray(valor) ? valor.filter((x): x is number => typeof x === 'number') : []
  } catch {
    return []
  }
}
